// Mock progress tracking service for lessons
class LessonsProgressService {
  constructor() {
    this.storageKey = 'examlock_lessons_progress';
  }

  // Get all progress data
  getAllProgress() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error reading lessons progress:', error);
      return {};
    }
  }

  // Get progress for a specific lesson
  getLessonProgress(lessonId) {
    const allProgress = this.getAllProgress();
    return allProgress[lessonId] || {
      progress: 0,
      completed: false,
      timeSpent: 0,
      lastAccessed: null,
      currentSlide: 0
    };
  }

  // Update lesson progress
  updateLessonProgress(lessonId, updates) {
    try {
      const allProgress = this.getAllProgress();
      const currentProgress = this.getLessonProgress(lessonId);

      const updatedProgress = {
        ...currentProgress,
        ...updates,
        lastAccessed: new Date().toISOString()
      };

      // Auto-complete if progress reaches 100%
      if (updatedProgress.progress >= 100) {
        updatedProgress.completed = true;
      }

      allProgress[lessonId] = updatedProgress;
      localStorage.setItem(this.storageKey, JSON.stringify(allProgress));

      return updatedProgress;
    } catch (error) {
      console.error('Error updating lesson progress:', error);
      return this.getLessonProgress(lessonId);
    }
  }

  // Mark lesson as started
  startLesson(lessonId) {
    return this.updateLessonProgress(lessonId, {
      progress: Math.max(this.getLessonProgress(lessonId).progress, 10)
    });
  }

  // Update current slide and progress
  updateSlideProgress(lessonId, currentSlide, totalSlides) {
    const progressPercentage = Math.round(((currentSlide + 1) / totalSlides) * 100);
    return this.updateLessonProgress(lessonId, {
      currentSlide,
      progress: progressPercentage
    });
  }

  // Mark lesson as completed
  completeLesson(lessonId) {
    return this.updateLessonProgress(lessonId, {
      progress: 100,
      completed: true
    });
  }

  // Get overall progress stats
  getOverallStats() {
    const allProgress = this.getAllProgress();
    const lessons = Object.keys(allProgress);

    const completed = lessons.filter(id => allProgress[id].completed).length;
    const inProgress = lessons.filter(id => allProgress[id].progress > 0 && !allProgress[id].completed).length;
    const totalTime = lessons.reduce((sum, id) => sum + (allProgress[id].timeSpent || 0), 0);

    return {
      totalLessons: lessons.length,
      completed,
      inProgress,
      notStarted: Math.max(0, lessons.length - completed - inProgress),
      totalTimeSpent: totalTime,
      averageProgress: lessons.length > 0
        ? Math.round(lessons.reduce((sum, id) => sum + allProgress[id].progress, 0) / lessons.length)
        : 0
    };
  }

  // Get subject-specific stats
  getSubjectStats(subject) {
    const allProgress = this.getAllProgress();
    const subjectLessons = Object.keys(allProgress).filter(id => id.startsWith(subject));

    const completed = subjectLessons.filter(id => allProgress[id].completed).length;
    const totalProgress = subjectLessons.reduce((sum, id) => sum + allProgress[id].progress, 0);

    return {
      totalLessons: subjectLessons.length,
      completed,
      averageProgress: subjectLessons.length > 0
        ? Math.round(totalProgress / subjectLessons.length)
        : 0
    };
  }

  // Reset all progress (for testing)
  resetAllProgress() {
    try {
      localStorage.removeItem(this.storageKey);
      return true;
    } catch (error) {
      console.error('Error resetting progress:', error);
      return false;
    }
  }

  // Add some mock initial progress for demo
  initializeMockProgress() {
    const mockProgress = {
      'physics-2-5': {
        progress: 67,
        completed: false,
        timeSpent: 180,
        lastAccessed: new Date(Date.now() - 86400000).toISOString(), // Yesterday
        currentSlide: 2
      },
      'chemistry-1-1': {
        progress: 100,
        completed: true,
        timeSpent: 320,
        lastAccessed: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
        currentSlide: 3
      }
    };

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(mockProgress));
      return true;
    } catch (error) {
      console.error('Error initializing mock progress:', error);
      return false;
    }
  }
}

// Export singleton instance
export const lessonsProgressService = new LessonsProgressService();
export default lessonsProgressService;